import { AxiosError } from "axios";
import { ReactNode, createContext, useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, errorTreatment } from "../../services/api";
import { AuthContext } from "../Authentication/AuthContext";
import { LoadingContext } from "../Loading/LoadingContext";
import { Dashboard } from "../Dashboard/DashboardContext";

interface HomeContextData {
  completeRequisition: boolean;
  addFromTemplate: (content: TemplateProps["content"]) => void;
  ImportDashboard: (content: Dashboard) => void;
  duplicateDashboard: (id: string) => void;
  exportDashboard: (
    id: string
  ) => Promise<Dashboard | undefined>;
}

export interface TemplateProps {
  id: string;
  name: string;
  description: string;
  content: {
    id?: string;
    title: string;
    filters?: {
      regions: string[];
      shippers: string[];
      couriers: string[];
      clients: number[];
      routeTypes: string[];
    };
    graphics: {
      h: number;
      i: string;
      w: number;
      x: number;
      y: number;
      moved: boolean;
      static: boolean;
    }[];
  }[];
  image: string;
  accounts_excluded: string;
}

interface HomeProviderProps {
  children: ReactNode;
}

export const HomeContext = createContext({} as HomeContextData);

export function HomeProvider({ children }: HomeProviderProps) {
  const [completeRequisition, setCompleteRequisition] = useState(false);
  const navigate = useNavigate();
  const { changeVisibility } = useContext(LoadingContext);
  const { hasShortToken } = useContext(AuthContext);
  const { shortToken } = useParams();

  const addFromTemplate = (content: TemplateProps["content"]) => {
    changeVisibility(true);
    Promise.all([
      api.post(`/dashboards/configurations`, {
        graphics: content[0].graphics,
        filters: content[0].filters,
        title: content[0].title,
      }),
    ])
      .then(([responseAdd]: [{ data: { id: string } }]) => {
        navigate(`/dashboard/edit/${responseAdd.data.id}/new`);
      })
      .catch((error: AxiosError) => {
        errorTreatment(error);
      })
      .finally(() => {
        changeVisibility(false);
        setCompleteRequisition(true);
      });
  };

  const duplicateDashboard = (id: string) => {
    changeVisibility(true);
    let routeConfiguration = "";
    if (hasShortToken) {
      routeConfiguration = `/dashboards/${id}/configurations?short_token=${shortToken}`;
    } else {
      routeConfiguration = `/dashboards/${id}/configurations`;
    }
    Promise.all([api.get(routeConfiguration)])
      .then(([configuration]: [{ data: Dashboard }]) => {
        api
          .post(`/dashboards/configurations`, {
            graphics: configuration.data.graphics,
            filters: configuration.data.filters,
            title: configuration.data.title + " (cópia)",
          })
          .catch((error: AxiosError) => {
            errorTreatment(error);
            changeVisibility(false);
            setCompleteRequisition(true);
          })
          .then(() => {
            window.location.reload();
            changeVisibility(false);
            setCompleteRequisition(true);
          });
      })
      .catch((error: AxiosError) => {
        errorTreatment(error);
      });
  };

  async function exportDashboard(id: string) {
    changeVisibility(true);
    let exportCode: Dashboard | undefined;
    let routeConfiguration = "";
    if (hasShortToken) {
      routeConfiguration = `/dashboards/${id}/configurations?short_token=${shortToken}`;
    } else {
      routeConfiguration = `/dashboards/${id}/configurations`;
    }
    await Promise.all([api.get(routeConfiguration)])
      .then(([configuration]: [{ data: Dashboard }]) => {
        delete configuration.data.id;
        exportCode = configuration.data;
        changeVisibility(false);
      })
      .catch((error: AxiosError) => {
        errorTreatment(error);
      });
    return exportCode;
  }

  const ImportDashboard = (content: Dashboard) => {
    changeVisibility(true);
    Promise.all([
      api.post(`/dashboards/configurations`, {
        graphics: content.graphics,
        filters: content.filters,
        title: content.title,
      }),
    ])
      .then(([responseAdd]: [{ data: { id: string } }]) => {
        navigate(`/dashboard/edit/${responseAdd.data.id}/new`);
      })
      .catch((error: AxiosError) => {
        errorTreatment(error);
      })
      .finally(() => {
        changeVisibility(false);
        setCompleteRequisition(true);
      });
  };

  return (
    <HomeContext.Provider
      value={{
        completeRequisition,
        addFromTemplate,
        duplicateDashboard,
        exportDashboard,
        ImportDashboard,
      }}
    >
      {children}
    </HomeContext.Provider>
  );
}
